import LegalPageShell from "@/components/site/LegalPageShell";
import { useLanguage, pick } from "@/lib/i18n";
import { SUPPORT_EMAIL, OWNER_NAME, OWNER_ADDRESS } from "@/lib/translations";

const COPY = {
    en: {
        title: "Terms of Service",
        subtitle:
            "The rules that govern your use of the Dino Detective app, this website and the one-time premium upgrade.",
        sections: [
            { id: "acceptance", label: "Acceptance" },
            { id: "usage", label: "App usage" },
            { id: "premium", label: "Premium purchase" },
            { id: "model", label: "One-time purchase model" },
            { id: "ip", label: "Intellectual property" },
            { id: "responsibilities", label: "User responsibilities" },
            { id: "liability", label: "Limitation of liability" },
            { id: "changes", label: "Changes to services" },
            { id: "law", label: "Governing law" },
            { id: "contact", label: "Contact" },
        ],
    },
    de: {
        title: "Nutzungsbedingungen",
        subtitle:
            "Die Regeln für die Nutzung der Dino-Detective-App, dieser Website und des einmaligen Premium-Upgrades.",
        sections: [
            { id: "acceptance", label: "Annahme" },
            { id: "usage", label: "App-Nutzung" },
            { id: "premium", label: "Premium-Kauf" },
            { id: "model", label: "Einmalkauf-Modell" },
            { id: "ip", label: "Urheberrechte" },
            { id: "responsibilities", label: "Pflichten des Nutzers" },
            { id: "liability", label: "Haftungsbeschränkung" },
            { id: "changes", label: "Änderungen der Dienste" },
            { id: "law", label: "Anwendbares Recht" },
            { id: "contact", label: "Kontakt" },
        ],
    },
};

export default function TermsOfService() {
    const { lang } = useLanguage();
    const t = pick(COPY, lang);
    return (
        <LegalPageShell title={t.title} subtitle={t.subtitle} sections={t.sections}>
            {lang === "en" ? <EN /> : <DE />}
        </LegalPageShell>
    );
}

function EN() {
    return (
        <>
            <h2 id="acceptance">Acceptance</h2>
            <p>
                By installing, opening or using the Dino Detective mobile app or by visiting the website at <strong>dino-detective.app</strong>,
                you (and where you act on behalf of a child, the parent or legal guardian) agree to these Terms of Service. If you do not agree,
                please do not use the app or website.
            </p>

            <h2 id="usage">App usage</h2>
            <ul>
                <li>The app is provided for personal, non-commercial use by children and families.</li>
                <li>You agree to use the app and website lawfully and in line with these Terms.</li>
                <li>You will not attempt to reverse engineer, decompile or otherwise tamper with the app, except to the extent allowed by mandatory law.</li>
                <li>You will not use the app to harm, harass or endanger others.</li>
            </ul>

            <h2 id="premium">Premium purchase</h2>
            <p>
                The app may offer an optional premium upgrade unlocking additional content. Purchases are processed exclusively through Google
                Play Billing under Google’s own terms. Refund requests should be made through Google Play in the first instance, in line with
                Google’s refund policies and applicable consumer protection law.
            </p>

            <h2 id="model">One-time purchase model</h2>
            <ul>
                <li><strong>One-time purchase only.</strong> The premium upgrade is a single, non-recurring payment.</li>
                <li><strong>No subscription.</strong> There is nothing to cancel and nothing renews automatically.</li>
                <li><strong>Restore purchases.</strong> If you reinstall the app or use a new device with the same Google account, premium content can be restored.</li>
            </ul>

            <h2 id="ip">Intellectual property</h2>
            <p>
                The app, website, names, logos, illustrations, text, sounds, code and all related materials are protected by copyright,
                trademark and other intellectual property laws and remain the property of {OWNER_NAME} or the respective rights holders. You are
                granted a limited, personal, non-transferable, revocable licence to use the app and website in line with these Terms.
            </p>

            <h2 id="responsibilities">User responsibilities</h2>
            <ul>
                <li>Parents and legal guardians are responsible for supervising children’s use of the app and for any purchases made on their device.</li>
                <li>You agree not to misuse the support channels, send malicious content, or attempt to circumvent app security.</li>
                <li>You are responsible for keeping your device, Google account and any backups secure.</li>
            </ul>

            <h2 id="liability">Limitation of liability</h2>
            <p>
                The app and website are provided “as is” and “as available”. To the maximum extent permitted by applicable law, {OWNER_NAME}
                will not be liable for indirect, incidental, special, consequential or punitive damages, or for any loss of data, profits or
                goodwill arising from the use or inability to use the app or website. Nothing in these Terms limits liability for death,
                personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.
            </p>

            <h2 id="changes">Changes to services</h2>
            <p>
                We may update, improve or discontinue parts of the app and website to keep them safe, compliant and useful. We will not remove
                features that you have paid for without offering a fair alternative or refund, where applicable. Material changes to these Terms
                will be reflected on this page with a new “Last updated” date.
            </p>

            <h2 id="law">Governing law</h2>
            <p>
                These Terms are governed by the laws of the Federal Republic of Germany, without prejudice to mandatory consumer protection
                rules of the country where you reside. If you are a consumer, you may bring a claim before the courts of the country where you
                live.
            </p>

            <h2 id="contact">Contact</h2>
            <p>
                Questions about these Terms can be sent to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.<br />
                {OWNER_NAME}, {OWNER_ADDRESS}.
            </p>
        </>
    );
}

function DE() {
    return (
        <>
            <h2 id="acceptance">Annahme</h2>
            <p>
                Mit der Installation, dem Öffnen oder der Nutzung der Dino-Detective-App oder dem Besuch der Website{" "}
                <strong>dino-detective.app</strong> stimmst du (bzw. die Erziehungsberechtigten beim Handeln für ein Kind) diesen
                Nutzungsbedingungen zu. Wenn du nicht einverstanden bist, nutze die App oder Website bitte nicht.
            </p>

            <h2 id="usage">App-Nutzung</h2>
            <ul>
                <li>Die App ist für die persönliche, nicht-kommerzielle Nutzung durch Kinder und Familien bestimmt.</li>
                <li>Du verpflichtest dich zu einer rechtmäßigen Nutzung gemäß diesen Bedingungen.</li>
                <li>Reverse Engineering oder Manipulationen sind außerhalb zwingender gesetzlicher Ausnahmen untersagt.</li>
                <li>Eine Nutzung zur Schädigung oder Belästigung anderer ist untersagt.</li>
            </ul>

            <h2 id="premium">Premium-Kauf</h2>
            <p>
                Die App kann ein optionales Premium-Upgrade anbieten. Zahlungen werden ausschließlich über Google Play Billing unter den
                Bedingungen von Google abgewickelt. Erstattungsanfragen sind grundsätzlich an Google Play zu richten, im Einklang mit Googles
                Richtlinien und geltendem Verbraucherschutzrecht.
            </p>

            <h2 id="model">Einmalkauf-Modell</h2>
            <ul>
                <li><strong>Einmaliger Kauf.</strong> Das Premium-Upgrade ist eine einmalige, nicht wiederkehrende Zahlung.</li>
                <li><strong>Kein Abonnement.</strong> Es muss nichts gekündigt werden und es verlängert sich nichts automatisch.</li>
                <li><strong>Wiederherstellung.</strong> Bei Neuinstallation oder neuem Gerät mit demselben Google-Konto lassen sich Premium-Inhalte wiederherstellen.</li>
            </ul>

            <h2 id="ip">Urheberrechte</h2>
            <p>
                Die App, die Website sowie alle zugehörigen Namen, Logos, Illustrationen, Texte, Klänge und Codes sind durch das Urheber-, Marken-
                und sonstiges Recht geschützt und Eigentum von {OWNER_NAME} bzw. der jeweiligen Rechteinhaber. Du erhältst eine begrenzte,
                persönliche, nicht übertragbare Lizenz zur Nutzung gemäß diesen Bedingungen.
            </p>

            <h2 id="responsibilities">Pflichten des Nutzers</h2>
            <ul>
                <li>Eltern und Erziehungsberechtigte beaufsichtigen die Nutzung durch Kinder und sind für Käufe auf ihrem Gerät verantwortlich.</li>
                <li>Der Missbrauch der Support-Kanäle und das Umgehen von Sicherheitsmechanismen sind untersagt.</li>
                <li>Die Sicherung von Gerät, Google-Konto und Backups liegt in deiner Verantwortung.</li>
            </ul>

            <h2 id="liability">Haftungsbeschränkung</h2>
            <p>
                App und Website werden „wie sie sind“ bereitgestellt. Soweit gesetzlich zulässig, haftet {OWNER_NAME} nicht für mittelbare,
                zufällige oder Folgeschäden, entgangene Gewinne oder Datenverluste. Eine Haftung für Vorsatz, grobe Fahrlässigkeit, Verletzung
                von Leben, Körper oder Gesundheit sowie nach dem Produkthaftungsgesetz bleibt unberührt.
            </p>

            <h2 id="changes">Änderungen der Dienste</h2>
            <p>
                Wir dürfen Teile der App und Website weiterentwickeln, anpassen oder einstellen, um sie sicher und nützlich zu halten. Bezahlte
                Funktionen werden nicht ohne fairen Ersatz oder Erstattung entfernt. Wesentliche Änderungen dieser Bedingungen werden auf dieser
                Seite mit aktualisiertem Datum kenntlich gemacht.
            </p>

            <h2 id="law">Anwendbares Recht</h2>
            <p>
                Es gilt deutsches Recht, unbeschadet zwingender Verbraucherschutzvorschriften deines Wohnsitzlandes. Als Verbraucher kannst du
                Klagen vor den Gerichten deines Wohnsitzlandes erheben.
            </p>

            <h2 id="contact">Kontakt</h2>
            <p>
                Fragen zu diesen Bedingungen bitte an <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.<br />
                {OWNER_NAME}, {OWNER_ADDRESS}.
            </p>
        </>
    );
}
